import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ProfileInfo from "../components/ProfileInfo";
import CompanyGrouping from "../components/CompanyGrouping";
import ExpiredConnectionsBanner from "../components/ExpiredConnectionsBanner";
import LoadingScreen from "../components/LoadingScreen";
import { useAppData } from "../context/AppDataContext";
import { startQuickBooksConnect } from "../utils/quickbooks";

function Companies() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { companies, setCompanies, loading, isSubscribed } = useAppData();

  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [uiError, setUiError] = useState(null);

  // --- 1. EFFECT: LISTEN FOR BACKEND REDIRECT ERRORS ---
  useEffect(() => {
    const status = searchParams.get("status");
    const messageKey = searchParams.get("message");

    if (status === "error") {
      // Map backend "slugs" to human-readable text
      // NOTE: these keys must match the backend's redirect slugs exactly
      // (see python_backend/app/routers/quickbooks.py `callback`)
      const errorMap = {
        connection_canceled: "QuickBooks connection was cancelled by the user.",
        internal_server_error: "Our server had trouble communicating with QuickBooks. Please try again.",
        invalid_state: "Security session expired. Please try connecting again.",
        session_expired: "Your session expired before QuickBooks could finish connecting. Please log in and try again.",
      };

      setUiError(errorMap[messageKey] || "An unexpected error occurred during setup.");

      // CLEANUP: Remove error params from URL bar so they don't persist on refresh
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("status");
      newParams.delete("message");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // --- 2. SUBSCRIPTION GUARD ---
  const requireSubscription = (action) => {
    if (!isSubscribed) {
      const goToProfile = window.confirm(
        "You need an active subscription to manage companies.\n\nGo to your profile to subscribe?"
      );
      if (goToProfile) navigate("/profile");
      return;
    }
    action();
  };

  // --- 3. ACTIONS ---
  const handleLinkMultipleAccounts = () => {
    setUiError(null); // Clear errors before starting a new flow
    requireSubscription(async () => {
      try {
        await startQuickBooksConnect();
      } catch (err) {
        setUiError("Failed to initiate QuickBooks login");
      }
    });
  };

  const handleRemoveCompany = (removedAccountId) => {
    requireSubscription(() => {
      setCompanies(prev =>
        prev.filter(acc => acc.accountId !== removedAccountId)
      );
    });
  };

  // --- 4. DERIVED COUNTS ---
  const all = companies || [];
  const expiredCompanies = all.filter((acc) => acc.active === false);
  const connectedCount = all.length - expiredCompanies.length;
  const groupCount = new Set(
    all.map((a) => a.groupName).filter((g) => g && g !== "Ungrouped")
  ).size;

  // --- 5. RENDER STATES ---
  if (loading) {
    return <LoadingScreen title="Loading companies..." />;
  }

  const errorBanner = uiError && (
    <div className="inline-alert">
      <span aria-hidden="true">⚠️</span>
      <span>{uiError}</span>
      <button
        type="button"
        className="alert-close"
        onClick={() => setUiError(null)}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );

  if (!all.length) {
    return (
      <div className="page--companies">
        <div className="companies-shell">
          {errorBanner}
          <div className="panel">
            <div className="empty-state">
              <div className="empty-icon" aria-hidden="true">🏦</div>
              <h2>No companies connected yet</h2>
              <p>
                Link a QuickBooks company to start generating consolidated
                financial reports.
              </p>
              <button className="btn btn-brand" onClick={handleLinkMultipleAccounts}>
                Connect QuickBooks
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page--companies">
      <div className="companies-shell">
        <header className="companies-header">
          <div>
            <p className="eyebrow">Workspace</p>
            <h1 className="companies-title">Companies</h1>
            <p className="companies-subtitle">
              Manage the QuickBooks companies feeding your reports, and organize
              them into groups.
            </p>
          </div>

          <button className="btn btn-brand" onClick={handleLinkMultipleAccounts}>
            <span aria-hidden="true">+</span> Connect company
          </button>
        </header>

        {errorBanner}

        <ExpiredConnectionsBanner
          companies={expiredCompanies}
          onAction={handleLinkMultipleAccounts}
        />

        <div className="stat-row">
          <div className="stat-tile">
            <p className="stat-label">Companies</p>
            <p className="stat-value">{all.length}</p>
          </div>

          <div className="stat-tile">
            <p className="stat-label">
              <span className="stat-dot stat-dot--good" aria-hidden="true" />
              Connected
            </p>
            <p className="stat-value">{connectedCount}</p>
          </div>

          <div className={`stat-tile${expiredCompanies.length ? " is-alert" : ""}`}>
            <p className="stat-label">
              <span
                className={`stat-dot stat-dot--${
                  expiredCompanies.length ? "critical" : "neutral"
                }`}
                aria-hidden="true"
              />
              Needs attention
            </p>
            <p className="stat-value">{expiredCompanies.length}</p>
          </div>

          <div className="stat-tile">
            <p className="stat-label">Groups</p>
            <p className="stat-value">{groupCount}</p>
          </div>
        </div>

        <section className="panel">
          <div className="panel-header">
            <div className="panel-header-text">
              <h2>
                Connected companies
                <span className="count-pill">{all.length}</span>
              </h2>
              <p>Data from these companies is available in your reports.</p>
            </div>
            <button
              className="btn btn-neutral btn-sm"
              onClick={handleLinkMultipleAccounts}
            >
              Add company
            </button>
          </div>

          <div className="panel-body">
            <ProfileInfo
              accounts={all}
              onRemove={handleRemoveCompany}
              onReconnect={handleLinkMultipleAccounts}
            />
          </div>
        </section>

        <section className="panel">
          <CompanyGrouping
            accounts={all}
            setAccounts={setCompanies}
            selectedAccounts={selectedAccounts}
            setSelectedAccounts={setSelectedAccounts}
            requireSubscription={requireSubscription}
          />
        </section>
      </div>
    </div>
  );
}

export default Companies;
